'use client';

import { useState } from 'react';
import { cn } from '@/app/utils';
import type { CategoryTabsSectionData } from '../../types';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell } from '../section-shared';

export function TabSwitch({ section }: { section: CategoryTabsSectionData }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabs = section.tabs ?? [];
  const activeTab = tabs[activeIndex] ?? tabs[0];

  if (!tabs.length || !activeTab) {
    return null;
  }

  return (
    <SectionShell>
      <div className="mb-12 w-full text-center sm:mb-8">
        <h2 className="font-semibold text-[40px] text-white leading-[1.15] sm:text-[28px]">{section.title}</h2>
        <PortableTextContent className="mt-4" value={section.description} />
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4">
        {tabs?.map((tab, index) => {
          const active = index === activeIndex;

          return (
            <button
              key={tab._key || `${tab.tabLabel}-${index}`}
              className={cn(
                'h-10 rounded-[10px] border px-4 font-medium text-sm transition-colors',
                active
                  ? 'border-white/60 text-white'
                  : 'border-white/25 text-white/55 hover:border-white/40 hover:text-white/80',
              )}
              onClick={() => setActiveIndex(index)}
              type="button"
            >
              {tab.tabLabel}
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex items-center overflow-hidden rounded-2xl bg-white/10 sm:flex-col">
        <div className="w-[445px] shrink-0 overflow-hidden sm:w-full">
          <MediaAsset className="aspect-[4/3] w-full object-cover" media={activeTab.media} />
        </div>

        <div className="flex flex-1 flex-col gap-6 px-12 py-10 sm:px-6 sm:py-6">
          <div className="flex flex-col gap-3">
            <h3 className="font-semibold text-white text-xl leading-[1.2]">{activeTab.title}</h3>
            <PortableTextContent className="leading-[1.5]" value={activeTab.description} />
          </div>
          {activeTab.button?.href && activeTab.button?.label && (
            <a
              className="inline-flex h-11 w-fit items-center justify-center rounded-lg bg-[#0086FF] px-6 font-medium text-sm text-white transition-colors hover:bg-[#0f91ff]"
              href={activeTab.button.href}
              rel={/^https?:\/\//.test(activeTab.button.href) ? 'noreferrer' : undefined}
              target={/^https?:\/\//.test(activeTab.button.href) ? '_blank' : undefined}
            >
              {activeTab.button.label}
            </a>
          )}
        </div>
      </div>
    </SectionShell>
  );
}
